"use client";

import { c as _c } from "react/compiler-runtime";
import { environmentManager, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { createTRPCClient, httpBatchStreamLink, httpLink, loggerLink, splitLink } from "@trpc/client";
import { useState } from "react";
import superjson from "superjson";
import { rateLimitLink } from "@/lib/ratelimit/trpc-link";
import { TRPCProvider as Provider } from "@/lib/trpc/client";
import { makeQueryClient } from "@/trpc/query-client";
import type { AppRouter } from "@domainstack/api";
let browserQueryClient: ReturnType<typeof makeQueryClient> | undefined;
function getQueryClient() {
  // Server: always make a new query client
  if (environmentManager.isServer()) return makeQueryClient();
  // Browser: make a new query client if we don't already have one
  // This is very important, so we don't re-make a new client if React
  // suspends during the initial render. This may not be needed if we
  // have a suspense boundary BELOW the creation of the query client
  if (!browserQueryClient) browserQueryClient = makeQueryClient();
  return browserQueryClient;
}
const getBaseUrl = () => {
  if (typeof window !== "undefined") return ""; // browser should use relative url
  if (process.env.VERCEL_URL) return `https://${process.env.VERCEL_URL}`; // SSR should use vercel url
  return `http://localhost:${process.env.PORT ?? 3000}`; // dev SSR should use localhost
};
function trpcHeaders() {
  const headers = new Headers();
  headers.set("x-trpc-source", "nextjs-react");

  // For SSR on Vercel with deployment protection:
  // Add bypass token to allow server-to-server calls
  if (environmentManager.isServer() && process.env.VERCEL_AUTOMATION_BYPASS_SECRET) {
    headers.set("x-vercel-protection-bypass", process.env.VERCEL_AUTOMATION_BYPASS_SECRET);
  }
  return headers;
}
export function TRPCProvider(t0) {
  const $ = _c(5);
  const {
    children
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = getQueryClient();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const queryClient = t1;
  const [trpcClient] = useState(_temp3);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = process.env.NODE_ENV === "development" && <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== children || $[3] !== trpcClient) {
    t3 = <QueryClientProvider client={queryClient}><Provider trpcClient={trpcClient} queryClient={queryClient}>{children}{t2}</Provider></QueryClientProvider>;
    $[2] = children;
    $[3] = trpcClient;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}
function _temp3() {
  const url = `${getBaseUrl()}/api/trpc`;
  const jsonLinkOptions = {
    url,
    transformer: superjson,
    headers: trpcHeaders
  };
  return createTRPCClient({
    links: [loggerLink({
      enabled: _temp
    }), rateLimitLink, splitLink({
      condition: _temp2,
      true: httpLink(jsonLinkOptions),
      false: httpBatchStreamLink({
        ...jsonLinkOptions,
        maxItems: 10,
        maxURLLength: 2083
      })
    })]
  });
}
function _temp2(op) {
  return op.type === "mutation";
}
function _temp(opts) {
  if (process.env.NODE_ENV === "development") {
    return true;
  }
  if (opts.direction === "down" && opts.result instanceof Error) {
    const {
      cause
    } = opts.result as {
      cause?: {
        digest?: string;
      };
    };
    if (cause?.digest === "HANGING_PROMISE_REJECTION") {
      return false;
    }
    return true;
  }
  return false;
}